/**
 * useCopyShift — state + handlers for "copy shift to multiple days" dialog.
 *
 * Unterstuetzt seit 2026-05-11 zwei Entity-Typen: 'shift' (Mitarbeiter-Einsatz)
 * und 'activity' (Teilnehmer-Aktivitaet). Beim Oeffnen wird der Typ per
 * `openCopyDialog(entity, { type })` gesetzt; `handleCopyShift` waehlt dann
 * den passenden API-Aufruf.
 */
import { useState } from 'react';
import { format, addDays, parseISO, eachDayOfInterval, isWeekend, endOfMonth } from 'date-fns';
import { toast } from 'sonner';
import { shiftsAPI, activitiesAPI } from '../../lib/api';

export function useCopyShift({ weekStart, onReload }) {
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [shiftToCopy, setShiftToCopy] = useState(null);
  const [entityType, setEntityType] = useState('shift'); // 'shift' | 'activity'
  const [targetMassnahmeId, setTargetMassnahmeId] = useState('');
  const [deleteSourceAfterCopy, setDeleteSourceAfterCopy] = useState(false);
  const [copyDays, setCopyDays] = useState([]);
  const [copyWeekStart, setCopyWeekStart] = useState(null);
  const [copyDateRange, setCopyDateRange] = useState({ start: '', end: '' });
  const [copyMode, setCopyMode] = useState('week');
  const [saving, setSaving] = useState(false);

  const openCopyDialog = (entity, options = {}) => {
    setShiftToCopy(entity);
    setEntityType(options.type || 'shift');
    setTargetMassnahmeId(entity?.massnahme_id || '');
    setDeleteSourceAfterCopy(false);
    setCopyDays([]);
    setCopyWeekStart(weekStart);
    setCopyDateRange({ start: '', end: '' });
    setCopyMode('week');
    setCopyDialogOpen(true);
  };

  const getCopyWeekDays = () => {
    if (!copyWeekStart) return [];
    return Array.from({ length: 5 }, (_, i) => addDays(copyWeekStart, i));
  };

  const handleCopyPrevWeek = () => setCopyWeekStart((prev) => addDays(prev, -7));
  const handleCopyNextWeek = () => setCopyWeekStart((prev) => addDays(prev, 7));

  const getWeekdaysInRange = (startDate, endDate) => {
    if (!startDate || !endDate) return [];
    const start = parseISO(startDate);
    const end = parseISO(endDate);
    if (start > end) return [];
    return eachDayOfInterval({ start, end })
      .filter((day) => !isWeekend(day))
      .map((day) => format(day, 'yyyy-MM-dd'));
  };

  const selectNextWeek = () => {
    const nextWeekStart = addDays(weekStart, 7);
    const days = Array.from({ length: 5 }, (_, i) => format(addDays(nextWeekStart, i), 'yyyy-MM-dd'));
    setCopyDays((prev) => [...new Set([...prev, ...days])]);
  };

  const selectNextTwoWeeks = () => {
    const week1Start = addDays(weekStart, 7);
    const week2Start = addDays(weekStart, 14);
    const days = [
      ...Array.from({ length: 5 }, (_, i) => format(addDays(week1Start, i), 'yyyy-MM-dd')),
      ...Array.from({ length: 5 }, (_, i) => format(addDays(week2Start, i), 'yyyy-MM-dd')),
    ];
    setCopyDays((prev) => [...new Set([...prev, ...days])]);
  };

  const selectUntilMonthEnd = () => {
    const today = shiftToCopy ? parseISO(shiftToCopy.date) : new Date();
    const monthEnd = endOfMonth(today);
    const days = eachDayOfInterval({ start: addDays(today, 1), end: monthEnd })
      .filter((day) => !isWeekend(day))
      .map((day) => format(day, 'yyyy-MM-dd'));
    setCopyDays((prev) => [...new Set([...prev, ...days])]);
  };

  const selectFromDateRange = () => {
    if (!copyDateRange.start || !copyDateRange.end) {
      toast.error('Bitte Start- und Enddatum eingeben');
      return;
    }
    const days = getWeekdaysInRange(copyDateRange.start, copyDateRange.end);
    // Bei Cross-Measure-Copy (andere Ziel-Maßnahme) darf der Originaltag mit
    // ausgewählt werden — sonst rausfiltern, um Doppel-Einsatz zu vermeiden.
    const isCrossMeasure = targetMassnahmeId && shiftToCopy && targetMassnahmeId !== shiftToCopy.massnahme_id;
    const shiftDate = isCrossMeasure ? null : shiftToCopy?.date;
    setCopyDays((prev) => [...new Set([...prev, ...days.filter((d) => d !== shiftDate)])]);
  };

  const selectWeekForCopy = () => {
    const days = getCopyWeekDays().map((d) => format(d, 'yyyy-MM-dd'));
    const isCrossMeasure = targetMassnahmeId && shiftToCopy && targetMassnahmeId !== shiftToCopy.massnahme_id;
    const shiftDate = isCrossMeasure ? null : shiftToCopy?.date;
    setCopyDays((prev) => [...new Set([...prev, ...days.filter((d) => d !== shiftDate)])]);
  };

  const clearCopyDays = () => setCopyDays([]);

  const toggleCopyDay = (dayStr) => {
    setCopyDays((prev) => (prev.includes(dayStr) ? prev.filter((d) => d !== dayStr) : [...prev, dayStr]));
  };

  const handleCopyShift = async () => {
    if (!shiftToCopy || copyDays.length === 0) {
      toast.error('Bitte wählen Sie mindestens einen Tag aus');
      return;
    }
    setSaving(true);
    try {
      let successCount = 0;
      let conflictCount = 0;
      const effectiveMassnahmeId = targetMassnahmeId || shiftToCopy.massnahme_id;
      for (const day of copyDays) {
        if (entityType === 'activity') {
          const payload = {
            massnahme_id: effectiveMassnahmeId,
            location_id: shiftToCopy.location_id,
            date: day,
            activity_type: shiftToCopy.activity_type,
            custom_label: shiftToCopy.custom_label || null,
            notes: shiftToCopy.notes || null,
            participant_count: shiftToCopy.participant_count || null,
            start_time: shiftToCopy.start_time || null,
            end_time: shiftToCopy.end_time || null,
          };
          try {
            await activitiesAPI.create(payload);
            successCount++;
          } catch (err) {
            console.error('Error copying activity:', err);
          }
        } else {
          const payload = {
            employee_id: shiftToCopy.employee_id,
            massnahme_id: effectiveMassnahmeId,
            location_id: shiftToCopy.location_id || null,
            room_id: shiftToCopy.room_id || null,
            date: day,
            start_time: shiftToCopy.start_time,
            end_time: shiftToCopy.end_time,
            notes: shiftToCopy.notes || '',
          };
          try {
            const response = await shiftsAPI.create(payload);
            if (response.data.has_conflict) conflictCount++;
            successCount++;
          } catch (err) {
            console.error('Error copying shift:', err);
          }
        }
      }
      const noun = entityType === 'activity' ? 'Aktivitäten' : 'Einsätze';
      // Verschieben: nach erfolgreicher Kopie das Original loeschen.
      let sourceDeleted = false;
      if (deleteSourceAfterCopy && successCount > 0 && shiftToCopy?.id) {
        try {
          if (entityType === 'activity') {
            await activitiesAPI.delete(shiftToCopy.id);
          } else {
            await shiftsAPI.delete(shiftToCopy.id);
          }
          sourceDeleted = true;
        } catch (err) {
          console.error('Error deleting source after copy:', err);
        }
      }

      const verb = sourceDeleted ? 'verschoben' : 'kopiert';
      if (conflictCount > 0) {
        toast.warning(`${successCount} ${noun} ${verb} (${conflictCount} mit Konflikten)`);
      } else if (sourceDeleted) {
        toast.success(`${successCount} ${noun} verschoben (Original gelöscht)`);
      } else {
        toast.success(`${successCount} ${noun} erfolgreich kopiert`);
      }
      setCopyDialogOpen(false);
      onReload();
    } catch (error) {
      toast.error('Fehler beim Kopieren');
    } finally {
      setSaving(false);
    }
  };

  return {
    copyDialogOpen, setCopyDialogOpen, shiftToCopy, entityType, copyDays, copyWeekStart,
    copyDateRange, setCopyDateRange, copyMode, saving,
    targetMassnahmeId, setTargetMassnahmeId,
    deleteSourceAfterCopy, setDeleteSourceAfterCopy,
    openCopyDialog, getCopyWeekDays, handleCopyPrevWeek, handleCopyNextWeek,
    getWeekdaysInRange, selectNextWeek, selectNextTwoWeeks, selectUntilMonthEnd,
    selectFromDateRange, selectWeekForCopy, clearCopyDays, toggleCopyDay,
    handleCopyShift,
  };
}
